<template>
  <div class="h-full min-h-40">
    <TOverlay
      class="z-30"
      :fixed="false"
      :show="[STATES.LOADING, STATES.SENDING].includes(currentState)"
    />

    <div
      v-if="[STATES.LOAD_ERROR, STATES.SEND_ERROR].includes(currentState)"
      class="mt-4"
    >
      <ApiResponseError :api-response-error="apiResponseError" />
    </div>

    <template v-if="item && bookmarks">
      <TTitle size="xs" classes="mb-6" leading="tight">
        {{ t('views.cottage-bookmarks.title') }}
      </TTitle>

      <CottageBookmarksDetail :item="item" :user-data-id="userId" />
      <CottageBookmarksForm :items="bookmarks" @submit="onSubmit" />
    </template>

    <SSpacer />
  </div>
</template>

<script setup lang="ts">
// Stores
import { useUserStore } from '~/modules/auth/stores/userStore'

// Fetchers
import fetchRuralityBookmarksSave from '~/modules/api/rurality/v3/bookmarks/save'
import fetchRuralityStatsCottage from '~/modules/api/rurality/v3/stats/cottage/new'
import fetchRuralityBookmarksLists from '~/modules/api/rurality/v3/bookmarks/lists'
import fetchUserIsLogged from '~/modules/api/rurality/v3/user/is-logged'

// Composables
import { useCottageBookmarks } from '~/modules/views/cottage-bookmarks/composables/useCottageBookmarks'
import { scrollToTop } from '~/composables/useAnimations'

// Entitites
import type { CottagePage } from '~/modules/views/cottage/entities/CottagePage'
import type { CottageBookmarks } from '~/modules/views/cottage-bookmarks/entities/CottageBookmarks'

const { fromRuralityBookmarksDetail, toRuralityBookmarksSave } =
  useCottageBookmarks()

type itemType = Pick<
  CottagePage,
  'media' | 'name' | 'village' | 'province' | 'id'
>

interface propsInterface {
  item: itemType
}

const props = defineProps<propsInterface>()
const emit = defineEmits(['submitSuccess'])

// Constants
const bookmarks = ref<CottageBookmarks | null>(null)
const apiResponseError: Ref<ApiResponseError | null> = ref(null)
const userStore = useUserStore()
const userId = ref<string | undefined>(undefined)

// State Machine
const STATES = {
  LOADING: 'LOADING',
  LOAD_ERROR: 'LOAD_ERROR',
  IDLE: 'IDLE',
  SENDING: 'SENDING',
  SEND_ERROR: 'SEND_ERROR',
}

const currentState: Ref<keyof typeof STATES> = ref('LOADING')

// Methods
const fetchBookmarks = async (id: string) => {
  const data = await fetchRuralityBookmarksLists({
    id,
  })

  if (data.success) {
    bookmarks.value = fromRuralityBookmarksDetail({
      dataIn: data.success.data,
    })
    currentState.value = 'IDLE'
  } else if (data.error) {
    apiResponseError.value = data.error
    currentState.value = 'LOAD_ERROR'
    scrollToTop()
  }
}

const sendStatsCottage = async () => {
  await fetchRuralityStatsCottage({
    cottageId: props.item.id,
    eventType: 'cottage_bookmark',
  })
}

const onSubmit = async () => {
  if (bookmarks.value !== null) {
    currentState.value = 'SENDING'

    const params = toRuralityBookmarksSave({
      dataIn: bookmarks.value,
      cottageUid: props.item.id || '',
    })

    const data = await fetchRuralityBookmarksSave(params)

    if (data.success) {
      await sendStatsCottage()
      const authUserIsLogged = await fetchUserIsLogged()

      const userStore = useUserStore()
      userStore.setUser(authUserIsLogged)
      emit('submitSuccess')
    } else {
      apiResponseError.value = data.error
      currentState.value = 'SEND_ERROR'
      scrollToTop()
    }
  }
}

const init = async () => {
  const user = userStore.getUserdata
  if (!user.logged) {
    apiResponseError.value = {
      status: 'error',
      statusCode: 400,
      message: '',
      errors: [],
    }
    currentState.value = 'LOAD_ERROR'
    scrollToTop()
  } else if (user.logged && user.id) {
    userId.value = user.id
    fetchBookmarks(user.id)
  }
}

const unsubscribe = userStore.$subscribe(() => {
  init()
})
onUnmounted(() => {
  unsubscribe()
})

onMounted(() => {
  init()
})
</script>
